import _ from 'lodash';
import './polyfills';

import 'react-hot-loader/patch';
import { AppContainer } from 'react-hot-loader';
import React from 'react';
import { createHistory } from 'history';
import ReactDOM from 'react-dom';
import { useRouterHistory } from 'react-router';
import { syncHistoryWithStore, routerMiddleware } from 'react-router-redux';
import throttleActions from 'redux-throttle';
import thunk from 'redux-thunk';

import rootReducer from './reducers';
import { EXPERIMENT_DISABLE_FIX_VIEWPORT } from './constants/experiments';
import { FETCH_NOTIFICATIONS_SUCCESS } from './actions/fetch-notifications';
import { setSiteInfo } from './actions/set-site-info';
import { UPDATE_USER_SUCCESS } from './actions/update-user';
import { NEW_NOTIFICATION } from './actions/user-socket';
import { fetchAndSetSitePages } from './actions/fetch-and-set-site-pages';
import Root from './containers/root';
import { setConfig } from './services/config';
import createRequest from './services/create-request';
import { fixViewport } from './services/fix-viewport';
import listenToAppInstall from './services/listen-to-app-install';
import listenToEditModeChange from './services/listen-to-edit-mode-change';
import listenToHistoryStateChange from './services/listen-to-history-state-change';
import listenToSettingsChange from './services/listen-to-settings-change';
import listenToMemberDetailsUpdated from './services/listen-to-member-details-updated';
import { parseGetParam } from './services/parse-get-param';
import requestCredentials from './services/request-credentials';
import createShowMessage from './services/create-show-message';
import { replace } from './services/router-utils';
import scrollOnHistoryChange from './services/scroll-on-history-change';
import uploadFile from './services/upload-file';
import Wix from './services/wix-sdk-polyfill';
import { isMobile, getInstance } from './services/wix-sdk-utils';
import fedopsLogger from './services/fedops-logger';
import * as appLifecycleService from './services/app-lifecycle-service';
import { isExperimentEnabled } from './selectors/experiments-selectors';
import { forumsUouBiMiddleware, forumsUsersBiMiddleware } from './bi-events/init-middleware';
import createWebsocketMiddleware from './middleware/create-websocket-middleware';
import modalMiddleware from './middleware/modal-middleware';
import publishMiddleware from './middleware/publish-middleware';
import redirectMiddleware from './middleware/redirect-middleware';
import uploadMiddleware from './middleware/upload-middleware';
import actionLoggerMiddleware, { shouldLogReduxActions } from './middleware/action-logger-middleware';
import configureStore from './store/configureStore';
import debounceActions from '@sarunas/redux-debounce';
import wixWs from 'wix-ws-client/dist/ws-client.es5';

setConfig(window.__CONFIG__);
const baseUrl = window.__BASE_PATH__ || '';
const instance = getInstance();
const locale = window.__LOCALE__;
const request = createRequest({ baseUrl, instance, locale });

const browserHistory = useRouterHistory(createHistory)({
  basename: window.__BASE_PATH__ || '',
});

const config = {
  url: window.__WS_CONFIG__.url,
  token: window.__WS_CONFIG__.token,
};
const wsClient = wixWs({ wsUrl: config.url, token: config.token });

const middlewares = [
  routerMiddleware(browserHistory),
  throttleActions(),
  debounceActions(),
  redirectMiddleware,
  uploadMiddleware(uploadFile, requestCredentials, request),
  thunk.withExtraArgument({
    request,
    baseUrl: window.__BASE_PATH__ || `${window.location.protocol}//${window.location.host}`,
    fedopsLogger,
  }),
  createWebsocketMiddleware(wsClient, Wix.Utils.getInstanceId()),
  forumsUouBiMiddleware,
  forumsUsersBiMiddleware,
  publishMiddleware(Wix.PubSub.publish, [UPDATE_USER_SUCCESS, NEW_NOTIFICATION, FETCH_NOTIFICATIONS_SUCCESS]),
  modalMiddleware(createShowMessage),
];

shouldLogReduxActions() && middlewares.push(actionLoggerMiddleware);
const store = configureStore(_.omit(window.__INITIAL_STATE__ || {}, ['textEditor']), middlewares, rootReducer);
const history = syncHistoryWithStore(browserHistory, store);

if (isMobile() && !isExperimentEnabled(store.getState(), EXPERIMENT_DISABLE_FIX_VIEWPORT)) {
  fixViewport();
  window.addEventListener('orientationchange', fixViewport);
}

Wix.getSiteInfo(data => {
  store.dispatch(setSiteInfo(data));
});

store.dispatch(fetchAndSetSitePages());
listenToAppInstall(store);
listenToEditModeChange(store);
listenToSettingsChange(store, true);
listenToHistoryStateChange(history, store);
listenToMemberDetailsUpdated(store);
scrollOnHistoryChange(history);

let lastLocation = {};
history.listen(location => (lastLocation = location));

const redirect = parseGetParam('redirect');
if (redirect) {
  store.dispatch(replace(`${lastLocation.pathname}?redirect=${redirect}`));
}

const renderApp = Application => {
  appLifecycleService.appIsLoading({ logInSdk: true });
  ReactDOM.render(
    <AppContainer>
      <Application store={store} history={history}/>
    </AppContainer>,
    document.getElementById('root'));
};

renderApp(Root);

if (module.hot) {
  module.hot.accept('./containers/root', () => {
    console.log('HOT RELOAD START');
    // While using 'separateCss: true', external css files must be manually reloaded
    document.querySelectorAll('link[href][rel=stylesheet]').forEach(link => {
      const nextStyleHref = link.href.replace(/(\?\d+)?$/, `?${Date.now()}`);
      link.href = nextStyleHref;
    });

    renderApp(require('./containers/root').default);
    console.log('HOT RELOAD END');
  });
}



// WEBPACK FOOTER //
// ./app/app.js